import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { X } from 'lucide-react';
import { z } from 'zod';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { SandboxTelemetryDashboard } from '@apps/web/routes/_app/admin/sandbox/telemetry_dashboard';
import {
    useAdminSandboxOrganizations,
    useAdminSandboxTelemetry,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const TELEMETRY_WINDOW_DAYS = [1, 7, 30] as const;

const sandboxSearchSchema = z.object({
    days: z.union([z.literal(1), z.literal(7), z.literal(30)]).default(7),
    organizationId: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/sandbox/')({
    head: () => ({ meta: [{ title: 'Sandbox Explorer | Phoebe Admin' }] }),
    component: AdminSandboxPage,
    validateSearch: sandboxSearchSchema,
});

function formatBytes(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KiB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

function formatTtl(seconds: number | null): string {
    if (seconds === null) {
        return 'unknown';
    }
    if (seconds === 0) {
        return 'expired';
    }
    if (seconds < 3600) {
        return `${Math.ceil(seconds / 60)}m`;
    }
    if (seconds < 86400) {
        return `${Math.ceil(seconds / 3600)}h`;
    }
    return `${Math.ceil(seconds / 86400)}d`;
}

function formatLastUsed(value: string | null): string {
    return value
        ? formatDistanceToNowStrict(new Date(value), { addSuffix: true })
        : 'never';
}

function windowLabel(days: number): string {
    return days === 1 ? '24 h' : `${days} d`;
}

function AdminSandboxPage() {
    const navigate = Route.useNavigate();
    const { days, organizationId } = Route.useSearch();
    const {
        data: telemetry,
        isLoading: isTelemetryLoading,
        hasRequestFailed: hasTelemetryRequestFailed,
    } = useAdminSandboxTelemetry({
        days,
        organizationId,
    });
    const {
        data: organizationList,
        error: organizationsError,
        isLoading: isOrganizationsLoading,
    } = useAdminSandboxOrganizations();

    const setOrganization = (value: string | undefined) => {
        void navigate({
            search: (current) => ({
                ...current,
                organizationId: value,
            }),
        });
    };

    const activeOrgName = organizationId
        ? (organizationList?.organizations.find(
              (organization) => organization.organization_id === organizationId,
          )?.organization_name ?? organizationId)
        : null;

    return (
        <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 p-4 md:p-6">
            <header className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-semibold tracking-tight">
                            Sandbox explorer
                        </h1>
                        <Badge variant="outline" className="text-xs">
                            Admin
                        </Badge>
                    </div>
                    <p className="text-muted-foreground max-w-3xl text-sm">
                        Modal billed cost, reliability, and resource pressure
                        for the v3 tool sandbox. Every sandbox tool call reports
                        a resource summary here after it completes.
                    </p>
                </div>
                <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                Organization
                            </label>
                            <div className="flex items-center gap-2">
                                <AdminOrgPicker
                                    value={organizationId}
                                    onValueChange={(value) =>
                                        setOrganization(value)
                                    }
                                />
                                {activeOrgName ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                            setOrganization(undefined)
                                        }
                                        className="text-muted-foreground h-8"
                                    >
                                        <X
                                            className="mr-1 h-3.5 w-3.5"
                                            aria-hidden={true}
                                        />
                                        Clear
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                Window
                            </label>
                            <div
                                className="bg-background flex items-center gap-1 rounded-md border p-0.5"
                                role="group"
                                aria-label="Telemetry window"
                            >
                                {TELEMETRY_WINDOW_DAYS.map((windowDays) => {
                                    const active = days === windowDays;
                                    return (
                                        <Button
                                            key={windowDays}
                                            size="sm"
                                            variant={
                                                active ? 'default' : 'ghost'
                                            }
                                            aria-pressed={active}
                                            className="h-7 px-3 text-xs"
                                            onClick={() => {
                                                void navigate({
                                                    search: (current) => ({
                                                        ...current,
                                                        days: windowDays,
                                                    }),
                                                });
                                            }}
                                        >
                                            {windowLabel(windowDays)}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {activeOrgName ? (
                        <div className="text-muted-foreground flex flex-col gap-1 text-xs">
                            <span className="tracking-wide uppercase">
                                Viewing
                            </span>
                            <span className="text-foreground text-sm font-medium">
                                {activeOrgName}
                            </span>
                        </div>
                    ) : null}
                </div>
            </header>
            <SandboxTelemetryDashboard
                data={telemetry}
                isLoading={isTelemetryLoading}
                hasRequestFailed={hasTelemetryRequestFailed}
                onSelectOrganization={setOrganization}
            />
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                        <CardTitle>
                            Organizations with sandbox activity
                        </CardTitle>
                        <CardDescription>
                            Durable copy; the live working copy may differ.
                            Click an organization for its per-org sandbox
                            detail.
                        </CardDescription>
                    </div>
                    <p className="text-muted-foreground text-xs italic">
                        “
                        {organizationList?.durable_copy_notice ??
                            'durable copy; the live working copy may differ'}
                        ”
                    </p>
                </CardHeader>
                <CardContent>
                    {isOrganizationsLoading ? (
                        <p className="text-muted-foreground text-sm">
                            Loading organizations…
                        </p>
                    ) : organizationsError ? (
                        <p className="text-destructive text-sm">
                            Unable to load organization list.
                        </p>
                    ) : organizationList?.organizations.length ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Organization</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead>Generation</TableHead>
                                    <TableHead className="text-right">
                                        Runs
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Durable files
                                    </TableHead>
                                    <TableHead>Last used</TableHead>
                                    <TableHead className="text-right">
                                        TTL
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {organizationList.organizations.map(
                                    (organization) => (
                                        <TableRow
                                            key={organization.organization_id}
                                        >
                                            <TableCell>
                                                <Link
                                                    className="font-medium underline-offset-4 hover:underline"
                                                    to="/admin/sandbox/$organizationId"
                                                    params={{
                                                        organizationId:
                                                            organization.organization_id,
                                                    }}
                                                    search={{ days }}
                                                >
                                                    {
                                                        organization.organization_name
                                                    }
                                                </Link>
                                                <div className="text-muted-foreground font-mono text-xs">
                                                    {
                                                        organization.organization_id
                                                    }
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        organization.state ===
                                                        'live'
                                                            ? 'success'
                                                            : 'secondary'
                                                    }
                                                >
                                                    {organization.state}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {organization.generation ?? '—'}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {
                                                    organization.runs_with_durable_files
                                                }
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatBytes(
                                                    organization.durable_bytes,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatLastUsed(
                                                    organization.last_used_at,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatTtl(
                                                    organization.ttl_seconds_remaining,
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ),
                                )}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            No durable sandbox files or live sandbox activity
                            found.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
